import React, { useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { getApiUrl } from '@/config';

export const DataTools: React.FC = () => {
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleClearAllAssignments = async () => {
        if (!confirm("⚠️ ATTENTION : Voulez-vous vraiment supprimer TOUS les chantiers ?")) {
            return;
        }

        const confirmation = prompt("Tapez 'SUPPRIMER' pour confirmer :");
        if (confirmation !== 'SUPPRIMER') {
            return;
        }

        setMessage(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/assignments/clear-all'), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: 'Chantiers supprimés.' });
                setTimeout(() => window.location.reload(), 2000);
            } else {
                setMessage({ type: 'error', text: data.error || 'Erreur' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Erreur connexion' });
        }
    };

    const handleResetDatabase = async () => {
        if (!confirm("⚠️ DANGER SUPRÊME ⚠️\n\nVoulez-vous vraiment RAZ (Remise À Zéro) la base de données ?\n\nCela va supprimer :\n- TOUS les techniciens\n- TOUS les chantiers\n- TOUS les utilisateurs\n\nVous repartirez sur une base VIDE (sauf l'admin actuel).")) {
            return;
        }

        const confirmation = prompt("Pour confirmer, tapez 'RAZ' :");
        if (confirmation !== 'RAZ') {
            return;
        }

        setMessage(null);
        try {
            // We reuse the restore endpoint with an empty set of data to clear most things,
            // or better, we create a specific endpoint. 
            // For now, let's assume we can use a "clear-all" logic via multiple delete calls or a new endpoint if available.
            // Since we don't have a specific "DELETE /api/db" endpoint, we will implement a soft reset by restoring an empty state via the restore endpoint
            // BUT wait, restore takes a file. 
            // Let's implement a clean 'Reset' via a fetch delete on multiple resources if possible.
            // Actually, looking at the server code, we have:
            // - DELETE /api/assignments/clear-all
            // - DELETE /api/users/:id
            // - DELETE /api/vehicles/:id
            // We DO NOT have a DELETE /api/technicians/clear-all.

            // So we will use a special trick: Upload an empty JSON file structure to the restore endpoint.
            const emptyDb = {
                technicians: [],
                users: [{ id: 'admin-1', username: 'admin', password: 'admin1234', role: 'admin' }], // Keep generic admin
                assignments: [],
                logs: [],
                subscriptions: []
            };

            const blob = new Blob([JSON.stringify(emptyDb)], { type: "application/json" });
            const file = new File([blob], "reset.json");
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/restore'), {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                setMessage({ type: 'success', text: 'Base de données réinitialisée. Rafraîchissement...' });
                setTimeout(() => window.location.reload(), 2000);
            } else {
                setMessage({ type: 'error', text: 'Erreur lors de la réinitialisation.' });
            }

        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: 'Erreur technique durant la RAZ.' });
        }
    };

    return (
        <div className="glass-panel p-6 border border-glass-border rounded-xl">
            <h2 className="text-xl font-bold text-gray-100 flex items-center gap-2 mb-4">
                <ShieldCheck className="text-primary" />
                Outils de Données
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Clear All Assignments Section */}
                <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <h3 className="font-semibold text-orange-400 mb-2 flex items-center gap-2">
                        <AlertTriangle size={18} />
                        Purger les Chantiers
                    </h3>
                    <p className="text-sm text-gray-400 mb-4">
                        Supprime tous les chantiers (backlog inclus).
                    </p>
                    <button
                        onClick={handleClearAllAssignments}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors w-full justify-center"
                    >
                        Purger Chantiers
                    </button>
                </div>

                {/* RESET COMPLETE DB */}
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                        <AlertTriangle size={18} />
                        RAZ Base de Données
                    </h3>
                    <p className="text-sm text-gray-400 mb-4">
                        Supprime TOUT (Techniciens, Users, Chantiers).
                    </p>
                    <button
                        onClick={handleResetDatabase}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors w-full justify-center"
                    >
                        TOUT EFFACER
                    </button>
                </div>

                {/* Cleanup Maintenance Tool - New Card */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg md:col-span-2 lg:col-span-1">
                    <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                        <ShieldCheck size={18} />
                        Maintenance Données
                    </h3>
                    <p className="text-sm text-gray-400 mb-4">
                        Supprime uniquement les interventions corrompues (sans date).
                    </p>
                    <button
                        onClick={async () => {
                            if (!confirm("Nettoyer les données invalides ?")) return;
                            try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(getApiUrl('/api/assignments/cleanup'), {
                                    method: 'DELETE',
                                    headers: { Authorization: `Bearer ${token}` }
                                });
                                const data = await res.json();
                                setMessage({ type: 'success', text: data.message });
                                setTimeout(() => window.location.reload(), 1500);
                            } catch (e) {
                                setMessage({ type: 'error', text: 'Echec du nettoyage' });
                            }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors w-full justify-center"
                    >
                        Réparer / Nettoyer
                    </button>
                </div>
            </div>

            {
                message && (
                    <div className={`mt-4 p-3 rounded-lg text-sm text-center font-medium ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {message.text}
                    </div>
                )
            }
        </div >
    );
};
